import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Typography } from "antd";
import {
  ShoppingOutlined,
  ArrowRightOutlined,
  LeftOutlined,
  RightOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

interface BannerImage {
  url: string;
  mobile_url?: string; // 📱 Auto-optimisée (800x1000, ratio 4:5)
  mobile_custom_url?: string; // 🆕 Custom uploadée par l'admin (1080x1350, ratio 4:5)
  title: string;
  description: string;
  order: number;
  optimized?: boolean;
  metadata?: any;
}

interface CarouselSettings {
  autoplay: boolean;
  autoplay_delay: number;
  show_dots: boolean;
  show_arrows: boolean;
  transition_effect: "slide" | "fade" | "zoom";
}

interface HeroBannerCarouselProps {
  bannerImages: BannerImage[];
  storeName: string;
  storeDescription?: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  carouselSettings?: CarouselSettings;
  onShopNow?: () => void;
  onExplore?: () => void;
}

const HeroBannerCarousel: React.FC<HeroBannerCarouselProps> = ({
  bannerImages = [],
  storeName,
  storeDescription,
  primaryColor = "#1890ff",
  secondaryColor = "#52c41a",
  fontFamily = "Inter",
  carouselSettings = {
    autoplay: true,
    autoplay_delay: 5000,
    show_dots: true,
    show_arrows: true,
    transition_effect: "slide",
  },
  onShopNow,
  onExplore,
}) => {
  // ✨ FIX CRITIQUE: Détection mobile IMMÉDIATE (avant premier render)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768;
    }
    return false;
  });

  // 🔧 FIX: Sur mobile, afficher UNIQUEMENT bannières avec mobile_custom_url ou mobile_url
  // Sur desktop, afficher toutes les bannières
  const filteredBannerImages = isMobile
    ? bannerImages.filter(b => b.mobile_custom_url || b.mobile_url)
    : bannerImages;
  const openProducts = onExplore || onShopNow;
  const handleBannerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if ((event.key === "Enter" || event.key === " ") && openProducts) {
      event.preventDefault();
      openProducts();
    }
  };

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(carouselSettings.autoplay);
  const [isVisible, setIsVisible] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Animation d'entrée
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Détection mobile avec listener pour changements orientation
  useEffect(() => {
    const checkMobile = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);
    };

    window.addEventListener("resize", checkMobile);
    window.addEventListener("orientationchange", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("orientationchange", checkMobile);
    };
  }, []);

  // 🚀 PHASE 2: LAZY LOADING DÉSACTIVÉ
  // Les bannières sont maintenant optimisées (35KB mobile, 93KB desktop)
  // Le lazy loading n'est plus nécessaire et causait des erreurs de chargement

  // Auto-play
  useEffect(() => {
    if (
      isPlaying &&
      carouselSettings.autoplay &&
      filteredBannerImages.length > 1
    ) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % filteredBannerImages.length);
      }, carouselSettings.autoplay_delay);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    isPlaying,
    carouselSettings.autoplay,
    carouselSettings.autoplay_delay,
    filteredBannerImages.length,
  ]);

  // Navigation
  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % filteredBannerImages.length);
  }, [filteredBannerImages.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(
      (prev) =>
        (prev - 1 + filteredBannerImages.length) % filteredBannerImages.length
    );
  }, [filteredBannerImages.length]);

  const handleCustomBannerClick = () => {
    if (Math.abs(touchStart - touchEnd) > 10) return;
    openProducts?.();
  };

  // S'il n'y a qu'une seule image ou aucune, pas besoin du carrousel
  if (!filteredBannerImages || filteredBannerImages.length === 0) {
    return null;
  }

  // Si une seule bannière, utiliser le composant simple
  if (filteredBannerImages.length === 1) {
    const singleBanner = filteredBannerImages[0];
    const usesCustomMobileBanner =
      isMobile && Boolean(singleBanner.mobile_custom_url);

    // 📱 Sélection intelligente avec priorité: mobile_custom > mobile_auto > desktop
    const singleBannerUrl = isMobile
      ? singleBanner.mobile_custom_url ||
        singleBanner.mobile_url ||
        singleBanner.url
      : singleBanner.url;

    // 🔍 DEBUG: Logs pour diagnostic single banner

    return (
      <div
        className="hero-banner-carousel hero-banner-carousel--single"
        onClick={usesCustomMobileBanner ? handleCustomBannerClick : undefined}
        onKeyDown={usesCustomMobileBanner ? handleBannerKeyDown : undefined}
        role={usesCustomMobileBanner ? "button" : undefined}
        tabIndex={usesCustomMobileBanner ? 0 : undefined}
        aria-label={
          usesCustomMobileBanner ? "Voir les produits de la boutique" : undefined
        }
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "100vw",
          ...(isMobile
            ? {
                height: "auto",
                minHeight: 0,
                aspectRatio: "4 / 5",
              }
            : {
                height: "auto",
                minHeight: 0,
                maxHeight: "none",
                aspectRatio: "12 / 5",
              }),
          overflow: "hidden",
          cursor: usesCustomMobileBanner ? "pointer" : undefined,
          background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}15)`,
          fontFamily: `${fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
        }}
      >
        {/* 🔧 FIX: Image directe au lieu de background-image pour meilleure compatibilité */}
        <img
          src={singleBannerUrl}
          alt={singleBanner.title || `Bannière ${storeName}`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            // 🔧 FIX CADRAGE: Centre parfait
            objectPosition: "center center",
          }}
          onError={(e) => {
            console.error(
              "❌ [BANNER ERROR] Échec chargement image:",
              singleBannerUrl
            );
            e.currentTarget.style.display = "none";
          }}
          onLoad={() => {}}
        />
        {/* Overlay gradient léger pour contraste texte */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              usesCustomMobileBanner
                ? "none"
                : "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.25) 100%)",
          }}
        />
        {/* Contenu identique au HeroBanner simple */}
        <div
          style={{
            position: "relative",
            height: "100%",
            display: "flex",
            alignItems: isMobile ? "flex-end" : "center",
            justifyContent: "center",
            padding: isMobile ? "1rem 1rem 1.25rem" : "2rem",
            textAlign: "center",
            zIndex: 10,
            pointerEvents: usesCustomMobileBanner ? "none" : undefined,
          }}
        >
          <div
            style={{
              maxWidth: "800px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {!usesCustomMobileBanner && (
              <Title
                level={1}
                style={{
                  color: "white",
                  margin: "0 0 1.5rem 0",
                  fontSize: isMobile
                    ? "1.35rem"
                    : "clamp(2.5rem, 5vw, 4rem)",
                  fontWeight: "800",
                  lineHeight: "1.1",
                  textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                {singleBanner.title || `Bienvenue chez ${storeName}`}
              </Title>
            )}
            {!isMobile && (singleBanner.description || storeDescription) && (
              <Text
                style={{
                  color: "rgba(255, 255, 255, 0.95)",
                    fontSize: isMobile ? "0.95rem" : "1.25rem",
                  objectFit: "cover",
                  display: "block",
                    marginBottom: isMobile ? "1rem" : "3rem",
                  textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                }}
              >
                {singleBanner.description || storeDescription}
              </Text>
            )}
            {isMobile && !usesCustomMobileBanner && (
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={onExplore || onShopNow}
                style={{
                  width: "100%",
                  maxWidth: 220,
                  height: 40,
                  padding: "0 16px",
                  border: "none",
                  borderRadius: 999,
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Voir les produits
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Touch/Swipe pour mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    const startPosition = e.targetTouches[0].clientX;
    setTouchStart(startPosition);
    setTouchEnd(startPosition);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  // Styles pour les transitions
  const getTransitionStyle = (index: number) => {
    const { transition_effect } = carouselSettings;

    switch (transition_effect) {
      case "fade":
        return {
          opacity: index === currentSlide ? 1 : 0,
          transform: "translateX(0)",
          transition: "opacity 0.8s ease-in-out",
        };

      case "zoom":
        return {
          opacity: index === currentSlide ? 1 : 0,
          transform: index === currentSlide ? "scale(1)" : "scale(1.1)",
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        };

      default: // slide
        return {
          transform: `translateX(${(index - currentSlide) * 100}%)`,
          transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        };
    }
  };

  const currentBanner =
    filteredBannerImages[currentSlide] || filteredBannerImages[0];
  const usesCustomMobileBanner =
    isMobile && Boolean(currentBanner.mobile_custom_url);
  const mobileIndicatorHeight =
    isMobile && carouselSettings.show_dots && filteredBannerImages.length > 1
      ? 34
      : 0;

  return (
    <div
      ref={carouselRef}
      className="hero-banner-carousel"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100vw",
        ...(isMobile
          ? {
              height: "auto",
              minHeight: 0,
              aspectRatio: "4 / 5",
              boxSizing: "content-box",
              paddingBottom: mobileIndicatorHeight,
            }
          : {
              height: "auto",
              minHeight: 0,
              maxHeight: "none",
              aspectRatio: "12 / 5",
            }),
        overflow: "hidden",
        background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}15)`,
        fontFamily: `${fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
        opacity: isVisible ? 1 : 0,
        transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Images du carrousel */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: isMobile ? mobileIndicatorHeight : 0,
          display: "flex",
          width:
            carouselSettings.transition_effect === "slide"
              ? `${filteredBannerImages.length * 100}%`
              : "100%",
        }}
      >
        {filteredBannerImages.map((banner, index) => {
          // 📱 Sélection intelligente URL avec priorité: mobile_custom > mobile_auto > desktop
          const bannerUrl = isMobile
            ? banner.mobile_custom_url || banner.mobile_url || banner.url
            : banner.url;
          const isClickableCustomMobileBanner =
            isMobile && Boolean(banner.mobile_custom_url) && Boolean(openProducts);

          // 🔍 DEBUG: Logs pour diagnostic
          if (index === 0) {
          }

          return (
            <div
              key={`banner-${index}`}
              className="banner-slide"
              onClick={
                isClickableCustomMobileBanner
                  ? handleCustomBannerClick
                  : undefined
              }
              onKeyDown={
                isClickableCustomMobileBanner ? handleBannerKeyDown : undefined
              }
              role={isClickableCustomMobileBanner ? "button" : undefined}
              tabIndex={
                isClickableCustomMobileBanner
                  ? index === currentSlide
                    ? 0
                    : -1
                  : undefined
              }
              aria-label={
                isClickableCustomMobileBanner
                  ? "Voir les produits de la boutique"
                  : undefined
              }
              style={{
                position:
                  carouselSettings.transition_effect === "slide"
                    ? "relative"
                    : "absolute",
                width:
                  carouselSettings.transition_effect === "slide"
                    ? `${100 / filteredBannerImages.length}%`
                    : "100%",
                height: "100%",
                overflow: "hidden",
                cursor: isClickableCustomMobileBanner ? "pointer" : undefined,
                ...getTransitionStyle(index),
              }}
            >
              {/* Image optimisée avec fallback */}
              <img
                src={bannerUrl}
                alt={banner.title || `Bannière ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center center", // 🔧 Centre parfait
                  display: "block",
                }}
                onError={(e) => {
                  console.error("❌ Erreur chargement bannière:", bannerUrl);
                  // Fallback vers URL desktop si mobile échoue
                  if (
                    isMobile &&
                    banner.mobile_url &&
                    e.currentTarget.src === banner.mobile_url
                  ) {
                    e.currentTarget.src = banner.url;
                  }
                }}
              />
              {/* Overlay gradient léger pour contraste texte */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    isMobile && banner.mobile_custom_url
                      ? "none"
                      : "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.25) 100%)",
                  pointerEvents: "none",
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Overlay décoratif supprimé pour clarté maximale */}

      {/* Contenu principal */}
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          alignItems: isMobile ? "flex-end" : "center",
          justifyContent: "center",
          padding: isMobile ? "1rem 1rem 4rem" : "2rem",
          textAlign: "center",
          zIndex: 10,
          pointerEvents: usesCustomMobileBanner ? "none" : undefined,
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            animation: "slideInUp 0.8s ease-out",
          }}
        >
          {/* Titre dynamique */}
          {!usesCustomMobileBanner && (
            <Title
              level={1}
              key={`title-${currentSlide}`}
              style={{
                color: "white",
                margin: "0 0 1.5rem 0",
                fontSize: isMobile
                  ? "1.35rem"
                  : "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "800",
                lineHeight: "1.1",
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                transition: "all 0.6s ease",
              }}
            >
              {currentBanner.title || `Bienvenue chez ${storeName}`}
            </Title>
          )}

          {/* Description dynamique */}
          {!isMobile && (currentBanner.description || storeDescription) && (
            <Text
              key={`desc-${currentSlide}`}
              style={{
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: isMobile ? "0.95rem" : "1.25rem",
                fontWeight: "400",
                lineHeight: "1.6",
                marginBottom: isMobile ? "1rem" : "3rem",
                display: "block",
                maxWidth: "600px",
                margin: isMobile ? "0 auto 1rem auto" : "0 auto 3rem auto",
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                transition: "all 0.6s ease",
              }}
            >
              {currentBanner.description || storeDescription}
            </Text>
          )}

          {/* Boutons CTA */}
          <div
            style={{
              display: "flex",
              gap: isMobile ? "10px" : "16px",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              flexDirection: isMobile ? "column" : "row",
              width: "100%",
            }}
          >
            {isMobile ? (
              !usesCustomMobileBanner ? (
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={onExplore || onShopNow}
                  style={{
                    width: "100%",
                    maxWidth: 220,
                    height: 40,
                    padding: "0 16px",
                    border: "none",
                    borderRadius: 999,
                    background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Voir les produits
                </Button>
              ) : null
            ) : (
              <>
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingOutlined />}
                  onClick={onShopNow}
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    border: "none",
                    borderRadius: "50px",
                    height: "60px",
                    padding: "0 40px",
                    fontSize: "16px",
                    fontWeight: "600",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Découvrir la boutique
                </Button>

                <Button
                  type="default"
                  size="large"
                  icon={<ArrowRightOutlined />}
                  onClick={onExplore}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "50px",
                    height: "60px",
                    padding: "0 40px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "white",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Explorer les produits
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Flèches de navigation */}
      {!isMobile && carouselSettings.show_arrows && filteredBannerImages.length > 1 && (
        <>
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={prevSlide}
            style={{
              position: "absolute",
              left: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              color: "white",
              fontSize: "18px",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
              e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "translateY(-50%) scale(1)";
            }}
          />

          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={nextSlide}
            style={{
              position: "absolute",
              right: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              color: "white",
              fontSize: "18px",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
              e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "translateY(-50%) scale(1)";
            }}
          />
        </>
      )}

      {/* Indicateurs (dots) */}
      {carouselSettings.show_dots && filteredBannerImages.length > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: isMobile ? "5px" : "30px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: isMobile ? "8px" : "12px",
            zIndex: 20,
            background: "rgba(0, 0, 0, 0.2)",
            padding: isMobile ? "6px 12px" : "8px 16px",
            borderRadius: "25px",
            backdropFilter: "blur(10px)",
          }}
        >
          {filteredBannerImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                width: index === currentSlide ? (isMobile ? "20px" : "24px") : isMobile ? "6px" : "8px",
                height: isMobile ? "6px" : "8px",
                borderRadius: "4px",
                border: "none",
                background:
                  index === currentSlide
                    ? `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`
                    : "rgba(255, 255, 255, 0.5)",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: index === currentSlide ? "scale(1)" : "scale(0.8)",
              }}
            />
          ))}
        </div>
      )}

      {/* Contrôle play/pause */}
      {!isMobile && carouselSettings.autoplay && filteredBannerImages.length > 1 && (
        <Button
          type="text"
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          onClick={() => setIsPlaying(!isPlaying)}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 20,
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            color: "white",
            fontSize: "16px",
            transition: "all 0.3s ease",
          }}
        />
      )}

      {/* Styles CSS intégrés */}
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* ✨ CSS RESPONSIVE OPTIMISÉ */
        .hero-banner-carousel {
          width: 100%;
          max-width: 100vw;
          display: block;
          overflow: hidden;
          contain: paint;
        }

        .banner-slide {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .banner-slide img {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }

        /* Smartphones portrait (< 576px) */
        @media (max-width: 575px) {
          .hero-banner-carousel {
            aspect-ratio: 4 / 5;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
          }
        }

        /* Smartphones paysage et tablettes portrait (576px - 768px) */
        @media (min-width: 576px) and (max-width: 768px) {
          .hero-banner-carousel {
            aspect-ratio: 4 / 5;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
          }
        }

        /* Tablettes paysage (768px - 992px) */
        @media (min-width: 769px) and (max-width: 992px) {
          .hero-banner-carousel {
            aspect-ratio: 12 / 5;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
          }
        }

        /* Desktop (> 992px) */
        @media (min-width: 993px) {
          .hero-banner-carousel {
            aspect-ratio: 12 / 5;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HeroBannerCarousel;



